import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { ActionToastContent } from '@/components/action-toast-content';
import { Analytics } from '@/utils/analytics';
import { AnalyticsHubFeedScreens } from '@/components/insights-hub/types';
import { ClearAllAlertsConfirmationActionSheet } from '@/components/insights-hub/clear-all-alerts-confirmation-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { HIDDEN_ALERTS_ROUTES } from '@/navigators/routes';
import { HiddenAlertsStackParams } from '@/navigators/types';
import { HubCardData } from '@/hooks/insights-hub/types';
import { HubFeedCardsList } from '@/components/insights-hub/hub-feed-cards-list';
import { ModalContainer } from '@/components/modal-container';
import { Platform, ScrollView, Text, TouchableOpacity } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { TYPOGRAPHY } from '@/styles';
import { useAnalyticsAlertActions } from '@/hooks/insights-hub/use-analytics-alerts-actions';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useHiddenAlertsData } from '@/hooks/insights-hub/use-hidden-alerts-data';
import { useHubCardNavigation } from '@/hooks/insights-hub/use-hub-card-navigation';
import { useToasts } from '@/contexts/toast-context';

export type Props = StackScreenProps<
  HiddenAlertsStackParams,
  HIDDEN_ALERTS_ROUTES.Base
>;

export const HiddenAlertsScreen: React.FC<Props> = ({ navigation }) => {
  const [styles] = useStyles();
  const { showErrorToast, showToast } = useToasts();
  const { data, loading, error } = useHiddenAlertsData();
  const { selectedRestaurantRelationship } = useAuthenticatedUserContext();
  const { deleteHiddenAlerts, undeleteHiddenAlerts } = useAnalyticsAlertActions(
    selectedRestaurantRelationship.supplierUUID,
  );
  const { navigateToRelevantScreen, navigateToViewAccount } =
    useHubCardNavigation();
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);

  const onPress = (cardData: HubCardData) => {
    navigateToViewAccount(cardData);
    Analytics.track('insights_hub.alert.press', {
      alertType: cardData.card_type,
      restaurantUUID: cardData.restaurant_uuid,
      screen: AnalyticsHubFeedScreens.HiddenAlerts,
    });
  };

  const onPressSupportInfo = (cardData: HubCardData) => {
    navigateToRelevantScreen(cardData);
    Analytics.track('insights_hub.alert.support_info.press', {
      alertType: cardData.card_type,
      restaurantUUID: cardData.restaurant_uuid,
      screen: AnalyticsHubFeedScreens.HiddenAlerts,
    });
  };

  const navigationHeaderProps = useMemo(() => {
    return {
      actions: [
        {
          component: (
            <TouchableOpacity
              onPress={() => {
                setShowClearConfirmation(true);
              }}
            >
              <Text style={[TYPOGRAPHY.h3, styles.clearText]}>
                {i18n.t('clear_all')}
              </Text>
            </TouchableOpacity>
          ),
        },
      ],
      showShadow: false,
      title: i18n.t('hidden'),
    };
  }, [styles]);

  if (!data || loading || error) {
    return null;
  }

  const onConfirmClearAll = async () => {
    try {
      // Close the confirmation sheet and navigate back to the hub FIRST, then await the mutation.
      // This allows us to make an optimistic update, and navigate the user out of the hidden alerts screen
      // assuming the mutation will succeed, while still handling possible errors raised by the mutation.
      setShowClearConfirmation(false);
      navigation.goBack();
      await deleteHiddenAlerts(data.map((card) => card.alert_uuid));
      Analytics.track('insights_hub.alert.delete_all.press', {
        alertUUIDs: data.map((card) => card.alert_uuid),
        platform: Platform.OS,
      });

      // show the confirmation toast with the undo button
      showToast({
        autoCloseTimeout: 0,
        content: (onClose) => (
          <ActionToastContent
            buttonProps={{
              children: i18n.t('undo'),
              onPress: async () => {
                onClose();
                await undeleteHiddenAlerts(data, 0);
                Analytics.track('insights_hub.alert.delete_all.undo.press', {
                  alertUUIDs: data.map((card) => card.alert_uuid),
                  platform: Platform.OS,
                });
              },
              trackingData: null,
            }}
            textContent={i18n.t('dsr_analytics.alerts_removed')}
          />
        ),
      });
    } catch (e) {
      showErrorToast();
    }
  };

  return (
    <ModalContainer navigationHeaderProps={navigationHeaderProps}>
      <ScrollView
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        style={styles.container}
      >
        <HubFeedCardsList
          alerts={data}
          containerStyle={styles.card}
          onPress={onPress}
          onPressSupportInfo={onPressSupportInfo}
        />
      </ScrollView>
      {showClearConfirmation ? (
        <ClearAllAlertsConfirmationActionSheet
          onClose={() => setShowClearConfirmation(false)}
          onConfirm={onConfirmClearAll}
        />
      ) : null}
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  card: {
    borderRadius: 8,
    marginBottom: 8,
  },
  clearText: {
    color: theme.sk.color.text.primary,
    fontSize: 16,
  },
  container: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    padding: 16,
  },
  contentContainer: {
    paddingBottom: 56,
  },
}));
